import { useSelector, useDispatch } from "react-redux";
import { useMemo } from "react";

import type {
  QuestionMeta,
  UseQuestionHookReturn,
} from "../QuestionProvider/questionHookTypes";
import {
  singleSelectActions,
  selectSingleSelectState,
  type SingleSelectSliceState,
} from "./SingleSelectSlice";

export interface SingleSelectState {
  selectedOptionIndex?: number;
  submittedOptionIndexes: number[];
}

export interface SingleSelectActions {
  selectOption: (optionIndex: number) => void;
  submit: () => void;
  reset: () => void;
}

export const useSingleSelect = (
  id: string | number | undefined
): UseQuestionHookReturn<SingleSelectState, SingleSelectActions> => {
  const dispatch = useDispatch();
  const stringId = String(id ?? "default");

  const rawState = useSelector((s: { cscq: SingleSelectSliceState }) =>
    selectSingleSelectState(s, stringId)
  );

  const state: SingleSelectState = useMemo(
    () => ({
      selectedOptionIndex: rawState.selected,
      submittedOptionIndexes: rawState.disabled,
    }),
    [rawState.selected, rawState.disabled]
  );

  const actions = useMemo<SingleSelectActions>(
    () => ({
      selectOption: (optionIndex: number) =>
        dispatch(
          singleSelectActions.selectOption({
            componentID: stringId,
            optionID: optionIndex,
          })
        ),
      submit: () => dispatch(singleSelectActions.submit(stringId)),
      reset: () => dispatch(singleSelectActions.reset(stringId)),
    }),
    [dispatch, stringId]
  );

  const meta: QuestionMeta = useMemo(() => ({ id: stringId }), [stringId]);

  return [state, actions, meta];
};
